/**
 * Admin → Notify (Email + SMS)
 *
 * Two sections in one page:
 *   1. Email providers - list + create (5 kinds) + delete + test send.
 *      Priority-ranked fallback chain; tenant can enable/disable rows.
 *   2. SMS settings - single-row Twilio config + TRAI DLT IDs for SEBI.
 *
 * Secrets opaque on read: server returns `*Set` booleans, never the
 * plaintext. Type a new value into the field to rotate; leave it
 * blank to preserve.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  Loader2,
  Mail,
  MessageSquare,
  Plus,
  RefreshCw,
  Save,
  Send,
  Trash2,
  XCircle,
} from 'lucide-react';

import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import {
  createEmailProvider,
  deleteEmailProvider,
  getSmsSettings,
  listEmailProviders,
  sendTestEmail,
  sendTestSms,
  upsertSmsSettings,
  type EmailProvider,
  type EmailProviderKind,
  type SmsSettings,
} from '@/lib/notify-admin-api';

export function Notify(): JSX.Element {
  return (
    <PageContainer>
      <PageHeader
        title="Notify"
        subtitle="Email + SMS providers. Email supports a priority-ranked fallback chain; SMS uses Twilio with TRAI DLT for SEBI CSCRF paging."
      />
      <div className="space-y-6">
        <EmailSection />
        <SmsSection />
      </div>
    </PageContainer>
  );
}

// ── Email ──

function EmailSection(): JSX.Element {
  const [providers, setProviders] = useState<EmailProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setProviders(await listEmailProviders());
    } catch (err) {
      setError(String((err as Error)?.message ?? err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  async function handleDelete(id: string): Promise<void> {
    if (!confirm('Delete this email provider?')) return;
    try { await deleteEmailProvider(id); await refresh(); }
    catch (err) { alert(String((err as Error)?.message ?? err)); }
  }

  return (
    <GlassCard>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>
              <Mail className="-mt-0.5 mr-2 inline h-4 w-4" />
              Email providers
            </CardTitle>
            <CardDescription>
              {providers.length} configured. Dispatcher tries enabled providers in priority order (ascending).
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" onClick={refresh} disabled={loading}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            </Button>
            <Button size="sm" onClick={() => setShowCreate(true)}>
              <Plus className="-mt-0.5 mr-1 inline h-3 w-3" />
              Add
            </Button>
          </div>
        </div>
      </CardHeader>
      <div className="p-5 pt-0 space-y-3">
        {error && (
          <div className="rounded-md bg-red-500/10 border border-red-500/30 p-3 text-xs text-red-500">
            <AlertTriangle className="-mt-0.5 mr-1 inline h-3 w-3" />
            {error}
          </div>
        )}
        {providers.length === 0 && !loading && (
          <div className="text-xs text-muted-foreground italic">No email providers configured.</div>
        )}
        {providers.map((p) => (
          <ProviderRow key={p.id} p={p} onDelete={handleDelete} />
        ))}
        <TestEmailPanel disabled={providers.filter((p) => p.enabled).length === 0} />
      </div>
      {showCreate && (
        <CreateProviderDialog onClose={() => setShowCreate(false)} onDone={refresh} />
      )}
    </GlassCard>
  );
}

function ProviderRow({ p, onDelete }: { p: EmailProvider; onDelete: (id: string) => void }): JSX.Element {
  return (
    <div className="rounded-lg border border-border bg-background/40 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold">{p.displayName}</span>
            <Badge variant="outline" className="text-[10px] uppercase">{p.providerKind}</Badge>
            {p.enabled ? (
              <Badge variant="success" className="text-[10px]">
                <CheckCircle2 className="-mt-0.5 mr-1 inline h-3 w-3" />Enabled
              </Badge>
            ) : (
              <Badge variant="warning" className="text-[10px]">
                <Clock className="-mt-0.5 mr-1 inline h-3 w-3" />Disabled
              </Badge>
            )}
            <Badge variant="outline" className="text-[10px]">priority {p.priority}</Badge>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            From <code>{p.fromAddress}</code>{p.fromName ? ` · ${p.fromName}` : ''}
          </p>
          {p.lastFailureMessage && (
            <p className="mt-1 text-xs text-red-500">
              <XCircle className="-mt-0.5 mr-1 inline h-3 w-3" />
              Last failure: {p.lastFailureMessage}
            </p>
          )}
        </div>
        <Button size="sm" variant="ghost" onClick={() => onDelete(p.id)} title="Delete" data-action="disc.adm_notify.a01">
          <Trash2 className="h-4 w-4 text-red-500" />
        </Button>
      </div>
    </div>
  );
}

function TestEmailPanel({ disabled }: { disabled: boolean }): JSX.Element {
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('FOCTTA Discovery - test email');
  const [body, setBody] = useState('This is a test email sent from the admin Notify page.');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function send(): Promise<void> {
    setError(null); setResult(null); setSending(true);
    try {
      const r = await sendTestEmail({ to, subject, body });
      setResult(`Sent via ${r.providerKind} (attempt ${r.attemptNumber}). messageId: ${r.providerMessageId}`);
    } catch (err) {
      setError(String((err as Error)?.message ?? err));
    } finally { setSending(false); }
  }

  return (
    <div className="mt-4 rounded-lg border border-border bg-background/20 p-4">
      <h4 className="mb-2 text-sm font-semibold">Test send</h4>
      <div className="space-y-2">
        <input className="w-full rounded-md border border-border bg-background/40 p-2 text-xs" value={to} onChange={(e) => setTo(e.target.value)} placeholder="recipient@example.com" />
        <input className="w-full rounded-md border border-border bg-background/40 p-2 text-xs" value={subject} onChange={(e) => setSubject(e.target.value)} />
        <textarea rows={3} className="w-full rounded-md border border-border bg-background/40 p-2 text-xs" value={body} onChange={(e) => setBody(e.target.value)} />
        <Button size="sm" onClick={send} disabled={sending || disabled || !to} data-action="disc.adm_notify.a02">
          {sending ? <Loader2 className="-mt-0.5 mr-1 inline h-3 w-3 animate-spin" /> : <Send className="-mt-0.5 mr-1 inline h-3 w-3" />}
          Send test
        </Button>
        {disabled && (
          <p className="text-[10px] text-muted-foreground italic">No enabled providers - enable at least one above to test.</p>
        )}
        {result && <p className="text-xs text-emerald-500"><CheckCircle2 className="-mt-0.5 mr-1 inline h-3 w-3" />{result}</p>}
        {error && <p className="text-xs text-red-500"><XCircle className="-mt-0.5 mr-1 inline h-3 w-3" />{error}</p>}
      </div>
    </div>
  );
}

function CreateProviderDialog({ onClose, onDone }: { onClose: () => void; onDone: () => Promise<void> }): JSX.Element {
  const [kind, setKind] = useState<EmailProviderKind>('resend');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Shared
  const [displayName, setDisplayName] = useState('');
  const [fromAddress, setFromAddress] = useState('');
  const [fromName, setFromName] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [priority, setPriority] = useState(1);

  // Per-kind
  const [smtpHost, setSmtpHost] = useState('');
  const [smtpPort, setSmtpPort] = useState(587);
  const [smtpUsername, setSmtpUsername] = useState('');
  const [smtpPassword, setSmtpPassword] = useState('');
  const [resendApiKey, setResendApiKey] = useState('');
  const [msalTenantId, setMsalTenantId] = useState('');
  const [msalClientId, setMsalClientId] = useState('');
  const [msalClientSecret, setMsalClientSecret] = useState('');
  const [msalAccountUpn, setMsalAccountUpn] = useState('');
  const [graphSenderUpn, setGraphSenderUpn] = useState('');
  const [googleClientId, setGoogleClientId] = useState('');
  const [googleClientSecret, setGoogleClientSecret] = useState('');
  const [googleRefreshToken, setGoogleRefreshToken] = useState('');
  const [googleSenderEmail, setGoogleSenderEmail] = useState('');

  async function submit(): Promise<void> {
    setErr(null); setSubmitting(true);
    try {
      const base: Record<string, unknown> = {
        providerKind: kind,
        displayName,
        fromAddress,
        ...(fromName ? { fromName } : {}),
        enabled,
        priority,
      };
      let payload: Record<string, unknown> = base;
      switch (kind) {
        case 'smtp':
          payload = { ...base, smtpHost, smtpPort, smtpUsername, smtpPassword };
          break;
        case 'resend':
          payload = { ...base, resendApiKey };
          break;
        case 'ms_smtp':
          payload = { ...base, msalTenantId, msalClientId, msalClientSecret, msalAccountUpn };
          break;
        case 'ms_graph':
          payload = { ...base, msalTenantId, msalClientId, msalClientSecret, graphSenderUpn };
          break;
        case 'google_smtp':
          payload = { ...base, googleClientId, googleClientSecret, googleRefreshToken, googleSenderEmail };
          break;
      }
      await createEmailProvider(payload);
      await onDone();
      onClose();
    } catch (e) {
      setErr(String((e as Error)?.message ?? e));
    } finally { setSubmitting(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-auto rounded-xl border border-border bg-background p-6 shadow-2xl">
        <h3 className="mb-3 text-lg font-semibold">Add email provider</h3>
        <div className="mb-4 flex flex-wrap gap-2">
          {(['resend','smtp','ms_graph','ms_smtp','google_smtp'] as const).map((k) => (
            <Button key={k} size="sm" variant={kind === k ? 'default' : 'outline'} onClick={() => setKind(k)}>
              {k}
            </Button>
          ))}
        </div>
        <div className="space-y-2">
          <Field label="Display name" value={displayName} onChange={setDisplayName} placeholder="e.g. Resend primary" />
          <Field label="From address" value={fromAddress} onChange={setFromAddress} placeholder="alerts@yourdomain.com" />
          <Field label="From name (optional)" value={fromName} onChange={setFromName} />
          <div className="flex items-center gap-4 text-xs">
            <label className="inline-flex items-center gap-2">
              <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
              Enabled
            </label>
            <label className="inline-flex items-center gap-2">
              Priority
              <input type="number" min={1} max={100} value={priority} onChange={(e) => setPriority(parseInt(e.target.value, 10) || 1)} className="w-16 rounded-md border border-border bg-background/40 p-1" />
            </label>
          </div>
          {kind === 'smtp' && (
            <>
              <Field label="SMTP host" value={smtpHost} onChange={setSmtpHost} />
              <div className="flex gap-2"><label className="text-xs">Port</label><input type="number" value={smtpPort} onChange={(e) => setSmtpPort(parseInt(e.target.value, 10) || 587)} className="rounded-md border border-border bg-background/40 p-2 text-xs" /></div>
              <Field label="SMTP username" value={smtpUsername} onChange={setSmtpUsername} />
              <Field label="SMTP password" value={smtpPassword} onChange={setSmtpPassword} type="password" />
            </>
          )}
          {kind === 'resend' && (
            <Field label="Resend API key" value={resendApiKey} onChange={setResendApiKey} type="password" placeholder="re_..." />
          )}
          {(kind === 'ms_smtp' || kind === 'ms_graph') && (
            <>
              <Field label="Azure AD tenant ID" value={msalTenantId} onChange={setMsalTenantId} />
              <Field label="Application (client) ID" value={msalClientId} onChange={setMsalClientId} />
              <Field label="Client secret" value={msalClientSecret} onChange={setMsalClientSecret} type="password" />
              {kind === 'ms_smtp' ? (
                <Field label="Account UPN (mailbox to authenticate as)" value={msalAccountUpn} onChange={setMsalAccountUpn} placeholder="noreply@yourdomain.onmicrosoft.com" />
              ) : (
                <Field label="Graph sender UPN" value={graphSenderUpn} onChange={setGraphSenderUpn} placeholder="alerts@yourdomain.onmicrosoft.com" />
              )}
            </>
          )}
          {kind === 'google_smtp' && (
            <>
              <Field label="OAuth Client ID" value={googleClientId} onChange={setGoogleClientId} />
              <Field label="OAuth Client Secret" value={googleClientSecret} onChange={setGoogleClientSecret} type="password" />
              <Field label="Refresh token" value={googleRefreshToken} onChange={setGoogleRefreshToken} type="password" />
              <Field label="Sender email" value={googleSenderEmail} onChange={setGoogleSenderEmail} />
            </>
          )}
          {err && <p className="text-xs text-red-500">{err}</p>}
          <div className="mt-4 flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button size="sm" onClick={submit} disabled={submitting || !displayName || !fromAddress} data-action="disc.adm_notify.a03">
              {submitting ? <Loader2 className="-mt-0.5 mr-1 inline h-3 w-3 animate-spin" /> : <Plus className="-mt-0.5 mr-1 inline h-3 w-3" />}
              Create
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── SMS ──

function SmsSection(): JSX.Element {
  const [settings, setSettings] = useState<SmsSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [accountSid, setAccountSid] = useState('');
  const [authToken, setAuthToken] = useState('');
  const [fromNumber, setFromNumber] = useState('');
  const [peId, setPeId] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [headerId, setHeaderId] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [testTo, setTestTo] = useState('');
  const [testBody, setTestBody] = useState('FOCTTA test SMS.');
  const [testResult, setTestResult] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const row = await getSmsSettings();
      setSettings(row);
      if (row) {
        setAccountSid(row.twilioAccountSid);
        setFromNumber(row.twilioFromNumber);
        setPeId(row.traiDltPeId ?? '');
        setTemplateId(row.traiDltTemplateId ?? '');
        setHeaderId(row.traiDltHeaderId ?? '');
        setEnabled(row.enabled);
      }
    } catch (err) {
      setError(String((err as Error)?.message ?? err));
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const newConfig = !settings;

  async function save(): Promise<void> {
    setSaving(true); setError(null);
    try {
      const row = await upsertSmsSettings({
        enabled,
        twilioAccountSid: accountSid,
        twilioFromNumber: fromNumber,
        ...(authToken ? { twilioAuthToken: authToken } : {}),
        ...(peId ? { traiDltPeId: peId } : {}),
        ...(templateId ? { traiDltTemplateId: templateId } : {}),
        ...(headerId ? { traiDltHeaderId: headerId } : {}),
      });
      setSettings(row);
      setAuthToken('');
    } catch (err) {
      setError(String((err as Error)?.message ?? err));
    } finally { setSaving(false); }
  }

  async function test(): Promise<void> {
    setTestResult(null);
    try {
      const r = await sendTestSms({ to: testTo, body: testBody });
      setTestResult(`Sent: SID ${r.providerMessageSid}, status ${r.status}`);
    } catch (err) {
      setTestResult(`Failed: ${String((err as Error)?.message ?? err)}`);
    }
  }

  return (
    <GlassCard>
      <CardHeader>
        <CardTitle>
          <MessageSquare className="-mt-0.5 mr-2 inline h-4 w-4" />
          SMS (Twilio) - SEBI CSCRF paging
        </CardTitle>
        <CardDescription>
          {newConfig ? 'Not configured.' : (settings?.enabled ? 'Enabled.' : 'Disabled - save with Enabled checked to activate.')}
          {' '}Indian numbers require TRAI DLT (PE ID + Template ID).
        </CardDescription>
      </CardHeader>
      <div className="space-y-3 p-5 pt-0">
        {error && (
          <div className="rounded-md bg-red-500/10 border border-red-500/30 p-3 text-xs text-red-500">
            <AlertTriangle className="-mt-0.5 mr-1 inline h-3 w-3" />{error}
          </div>
        )}
        <Field label="Twilio Account SID" value={accountSid} onChange={setAccountSid} placeholder="AC..." />
        <Field
          label={newConfig ? 'Auth Token' : 'New Auth Token (leave blank to preserve)'}
          value={authToken}
          onChange={setAuthToken}
          type="password"
        />
        <Field label="From number (E.164)" value={fromNumber} onChange={setFromNumber} placeholder="+12025550100 or DLT-registered sender for India" />
        <div className="grid grid-cols-3 gap-3">
          <Field label="TRAI PE ID (India)" value={peId} onChange={setPeId} />
          <Field label="TRAI Template ID" value={templateId} onChange={setTemplateId} />
          <Field label="TRAI Header ID" value={headerId} onChange={setHeaderId} />
        </div>
        <label className="inline-flex items-center gap-2 text-xs">
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          Enabled
        </label>
        <div>
          <Button size="sm" onClick={save} disabled={saving || !accountSid || !fromNumber || (newConfig && !authToken)} data-action="disc.adm_notify.a04">
            {saving ? <Loader2 className="-mt-0.5 mr-1 inline h-3 w-3 animate-spin" /> : <Save className="-mt-0.5 mr-1 inline h-3 w-3" />}
            Save
          </Button>
        </div>
        {settings && (
          <div className="mt-4 rounded-lg border border-border bg-background/20 p-4">
            <h4 className="mb-2 text-sm font-semibold">Test send</h4>
            <div className="space-y-2">
              <Field label="To (E.164)" value={testTo} onChange={setTestTo} placeholder="+919000000000" />
              <Field label="Body" value={testBody} onChange={setTestBody} />
              <Button size="sm" onClick={test} disabled={!testTo || !settings.enabled} data-action="disc.adm_notify.a05">
                <Send className="-mt-0.5 mr-1 inline h-3 w-3" />Send test
              </Button>
              {testResult && <p className="text-xs">{testResult}</p>}
            </div>
          </div>
        )}
      </div>
    </GlassCard>
  );
}

function Field(props: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: 'text' | 'password';
}): JSX.Element {
  return (
    <div>
      <label className="text-xs font-medium text-foreground/80">{props.label}</label>
      <input
        type={props.type ?? 'text'}
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        placeholder={props.placeholder}
        className="mt-1 w-full rounded-md border border-border bg-background/40 p-2 text-xs"
      />
    </div>
  );
}
